import { useState } from 'react'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import type { PopoverMenuTouchableOptionProps as Option } from 'modules/core/components/PopoverMenu'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import employeesCollection from 'modules/employees/store/collections/employees'
import GenerateSepaModal from 'modules/payroll_bundle/components/common/components/GenerateSepaModal'
import { usePaymentTotals } from 'modules/payroll_bundle/hooks/usePaymentTotals'
import { usePayrollContextlessPolicies } from 'modules/payroll_bundle/hooks/usePayrollContextlessPolicies'
import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { usePayrollPayments } from 'modules/payroll_bundle/lib/payrollPaymentsContext'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import bankAccounts from 'modules/payroll_bundle/store/collections/bankAccounts'
import paymentItems from 'modules/payroll_bundle/store/collections/paymentItems'
import type Payment from 'modules/payroll_bundle/store/models/Payment'
import {
  PAID,
  PAYMENT_SOURCE_EXTERNAL,
  PAYMENT_SOURCE_FACTORIAL,
  PENDING,
} from 'modules/payroll_bundle/store/models/Payment'

import DeletePaymentModal from './DeletePaymentModal'
import PaymentDetailSidepanel from './PaymentDetailSidepanel'

interface Props {
  payment: Payment
}

interface VisibleEmployee {
  userId: number
  amount: string
}

const ActionsPopover = ({ payment }: Props) => {
  const [openDownloadSepaModal, toggleOpenDownloadSepaModal] = useToggle(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const { showPaymentDetailsSidepanel, paymentDetailsToShow, paymentDispatch } =
    usePayrollPayments()
  const { currentPeriodStatus, currentPeriod, fetchCurrentPolicyPeriod } = usePolicyPeriods()
  const availableBankAccounts = bankAccounts.filter({
    archived: false,
  })
  const { refetch: refetchPaymentTotals } = usePaymentTotals(currentPeriod?.id)
  const paymentItemsByPaymentId = paymentItems.paymentItemByPaymentId(payment.id)
  const employees = paymentItemsByPaymentId.reduce(
    (visibleEmployees: VisibleEmployee[], paymentItem) => {
      const employee = paymentItem.has('employee_id')
        ? employeesCollection.get(paymentItem.attributes.employee_id)
        : null

      if (employee) {
        const userId = employee.attributes.access_id
        const amount = parseInt(paymentItem.attributes.amount_in_cents, 10) / 100

        visibleEmployees.push({
          userId,
          amount: amount.toString(),
        })
      }

      return visibleEmployees
    },
    []
  )

  const { canSeeCompanySepaGenerator } = usePayrollContextlessPolicies()

  const onClose = async () => {
    setShowDeleteModal(false)
    await refetchPaymentTotals()
  }

  const changePaymentStatus = async () => {
    const message =
      payment.attributes.status === PAID
        ? i18n.t('payroll_bundle.results_payments.payments.list.popover.unmarked')
        : i18n.t('payroll_bundle.results_payments.payments.list.popover.marked')
    await asyncAction(
      () =>
        payment.changeStatus({
          status: payment.attributes.status === PAID ? PENDING : PAID,
          paymentSource: PAYMENT_SOURCE_EXTERNAL,
        }),
      {
        successMessage: message,
      }
    )

    await Promise.all([refetchPaymentTotals(), fetchCurrentPolicyPeriod()])

    track(trackingEvents.payroll.markAsPaid, {
      period_status: currentPeriodStatus,
    })
  }

  const handlePaymentDetailsDisplay = (value: boolean) => {
    paymentDispatch({
      type: UPDATE_DATA,
      payload: {
        showPaymentDetailsSidepanel: value,
        paymentDetailsToShow: value ? payment : undefined,
      },
    })
  }

  const options: Option[] = [
    {
      onClick: () => handlePaymentDetailsDisplay(!showPaymentDetailsSidepanel),
      title: i18n.t('payroll_bundle.results_payments.payments.list.popover.see_details'),
    },
  ]

  if (payment.attributes.payment_source !== PAYMENT_SOURCE_FACTORIAL) {
    options.push(
      {
        onClick: changePaymentStatus,
        title:
          payment.attributes.status === PENDING
            ? i18n.t('payroll_bundle.results_payments.payments.list.popover.mark')
            : i18n.t('payroll_bundle.results_payments.payments.list.popover.unmark'),
      },
      {
        danger: true,
        onClick: () => setShowDeleteModal(true),
        title: i18n.t('payroll_bundle.results_payments.payments.list.popover.delete'),
      }
    )

    if (canSeeCompanySepaGenerator) {
      options.unshift({
        onClick: toggleOpenDownloadSepaModal(true),
        title: i18n.t('payroll_bundle.results_payments.payments.list.popover.download_sepa'),
      })
    }
  }

  return (
    <>
      <PopoverMenu type="list">
        <PopoverMenu.Trigger>
          <Box aria-label="action menu">
            <Button.Decorative type="secondary" iconName="ellipsis" />
          </Box>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="end">
          {options.map(({ title, onClick, danger }, index) => (
            <PopoverMenu.TouchableOption
              key={index}
              title={title}
              danger={danger}
              onClick={onClick}
            />
          ))}
        </PopoverMenu.Content>
      </PopoverMenu>
      {canSeeCompanySepaGenerator && openDownloadSepaModal && (
        <GenerateSepaModal
          employees={employees}
          selected={employees.map((employee) => employee.userId)}
          bankAccounts={availableBankAccounts}
          payment={payment}
          onClose={toggleOpenDownloadSepaModal(false)}
        />
      )}
      {showDeleteModal && <DeletePaymentModal onClose={onClose} payment={payment} />}
      {showPaymentDetailsSidepanel &&
        paymentDetailsToShow?.attributes.id === payment.attributes.id && (
          <PaymentDetailSidepanel
            isOpen={showPaymentDetailsSidepanel}
            onClose={() => handlePaymentDetailsDisplay(false)}
            payment={paymentDetailsToShow || payment}
          />
        )}
    </>
  )
}

export default observer(ActionsPopover)
